import React from 'react';
import { format } from 'timeago.js';
import { Diagram } from '../Icons';
import type { IProject } from '../../stores/projects/project';

/**
 * Render an item in the list of projects.
 *
 * Props:
 * - project: the project to render
 * - onDelete: callback to delete the project
 */
interface ProjectListItemProps {
  /** The project to render. */
  project: IProject;
  /** Callback to delete the project. */
  onDelete: (id: string) => void;
}

// `onDelete` is part of the caller contract (ProjectList passes it) but is not
// used in this presentational item — the delete button lives in ProjectList.
// Kept on Props for the contract; suppress the unused warning (same convention
// as TimeAgoTicker). Latent in the legacy JS too; carried over unchanged.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ProjectListItem = ({ project, onDelete }: ProjectListItemProps) => {
  // `lastEdited` is a luxon DateTime; `toISO()` is typed `string | null` (null
  // only for an invalid DateTime, which a persisted project never is), while
  // timeago's `format` wants a non-null TDate. Assert non-null — same convention
  // as AppBar/ProjectTitle.tsx; type-only, no runtime change.
  const lastEdited = format(project.lastEdited.toISO()!);
  return (
    <div className="flex items-center">
      <Diagram className="m-[10px] [flex:50px_0_0]" />
      <div className="flex-1 py-[5px] pl-[20px] text-[20px]">{project.displayName}</div>
      <div className="min-w-[120px] py-[5px] pl-[20px] opacity-70">Last edited {lastEdited}</div>
    </div>
  );
};

export default ProjectListItem;
